import {TranslateService} from '@ngx-translate/core';
import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {CompanyCostProductEnum, CompanyScheduledCost} from '@tj/tj-dmp-lib-api';
import {showValidationError} from '../../../form-validation';

@Component({
    selector: 'dmp-company-costs-form',
    templateUrl: './company-costs-form.component.html',
    styleUrls: ['./company-costs-form.component.scss'],
    standalone: false
})
export class CompanyCostsFormComponent implements OnChanges {
    get locale(): string { return this.translate.getCurrentLang() === 'en' ? 'en-GB' : 'fr-FR'; }


    @Input() costsForm: UntypedFormGroup | AbstractControl | null;
    @Input() submitted = false;

    isInvalid(control: AbstractControl | null): boolean {
        return showValidationError(control, this.submitted);
    }

    readonly contactPriceFields = [
        {controlName: 'defaultPrice', label: this.translate.instant('invoicing.common.default')},
        {controlName: 'flightPrice', label: this.translate.instant('invoicing.common.flight')},
        {controlName: 'hotelPrice', label: this.translate.instant('invoicing.common.hotel')},
        {controlName: 'carPrice', label: this.translate.instant('invoicing.common.car')},
        {controlName: 'parkingPrice', label: this.translate.instant('invoicing.common.parking')}
    ];

    readonly orderPriceFields = [
        {controlName: 'flightPrice', label: this.translate.instant('invoicing.common.flight')},
        {controlName: 'hotelPrice', label: this.translate.instant('invoicing.common.hotel')},
        {controlName: 'carPrice', label: this.translate.instant('invoicing.common.car')},
        {controlName: 'parkingPrice', label: this.translate.instant('invoicing.common.parking')}
    ];

    readonly costTypeOptions = [
        {label: this.translate.instant('invoicing.costs.purchase'), value: CompanyScheduledCost.CostTypeEnum.ACQUISITION},
        {label: this.translate.instant('invoicing.costs.sale'), value: CompanyScheduledCost.CostTypeEnum.SALE}
    ];

    readonly dataTypeOptions = [
        {label: this.translate.instant('invoicing.costs.contact'), value: CompanyScheduledCost.DataTypeEnum.CONTACTS},
        {label: this.translate.instant('invoicing.costs.order'), value: CompanyScheduledCost.DataTypeEnum.ORDERS}
    ];

    readonly productOptions = [
        {label: this.translate.instant('invoicing.common.default'), value: CompanyCostProductEnum.DEFAULT},
        {label: this.translate.instant('invoicing.common.flight'), value: CompanyCostProductEnum.FLIGHT},
        {label: this.translate.instant('invoicing.common.hotel'), value: CompanyCostProductEnum.HOTEL},
        {label: this.translate.instant('invoicing.common.car'), value: CompanyCostProductEnum.CAR},
        {label: this.translate.instant('invoicing.common.parking'), value: CompanyCostProductEnum.PARKING}
    ];

    constructor(private readonly translate: TranslateService, private readonly fb: UntypedFormBuilder) {
    }

    get form(): UntypedFormGroup | null {
        return this.costsForm instanceof UntypedFormGroup ? this.costsForm : null;
    }

    get scheduledCosts(): UntypedFormArray {
        return this.form?.get('scheduledCosts') as UntypedFormArray;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.costsForm) {
            this.applyScheduledCostValidators();
        }
    }

    getGroup(path: string): UntypedFormGroup {
        return this.form?.get(path) as UntypedFormGroup;
    }

    getScheduledCostGroup(control: AbstractControl): UntypedFormGroup {
        return control as UntypedFormGroup;
    }

    addScheduledCost(): void {
        this.scheduledCosts.push(this.buildScheduledCostForm());
    }

    removeScheduledCost(index: number): void {
        this.scheduledCosts.removeAt(index);
    }

    onScheduledDataTypeChange(group: UntypedFormGroup): void {
        if (
            group.get('dataType')?.value === CompanyScheduledCost.DataTypeEnum.ORDERS &&
            group.get('product')?.value === CompanyCostProductEnum.DEFAULT
        ) {
            group.get('product')?.setValue(null);
        }
        group.updateValueAndValidity();
    }

    isDefaultProductError(group: UntypedFormGroup): boolean {
        return group.hasError('defaultProductForOrders');
    }

    private buildScheduledCostForm(): UntypedFormGroup {
        return this.fb.group({
            costType: [CompanyScheduledCost.CostTypeEnum.SALE, Validators.required],
            dataType: [CompanyScheduledCost.DataTypeEnum.CONTACTS, Validators.required],
            product: [CompanyCostProductEnum.DEFAULT, Validators.required],
            price: [null, [Validators.required, Validators.min(0)]],
            effectiveDate: [null, Validators.required],
            applied: [{value: false, disabled: true}],
            appliedAt: [{value: null, disabled: true}]
        }, {validators: this.scheduledCostTargetValidator()});
    }

    private applyScheduledCostValidators(): void {
        this.scheduledCosts?.controls.forEach(control => {
            control.setValidators(this.scheduledCostTargetValidator());
            control.updateValueAndValidity({emitEvent: false});
        });
    }

    private scheduledCostTargetValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const group = control as UntypedFormGroup;
            const dataType = group.get('dataType')?.value;
            const product = group.get('product')?.value;

            if (dataType === CompanyScheduledCost.DataTypeEnum.ORDERS && product === CompanyCostProductEnum.DEFAULT) {
                return {defaultProductForOrders: true};
            }

            return null;
        };
    }
}
